/**
 * RoleIQ APEX Logo — three variants.
 * - LogoDark: for navy/dark backgrounds (nav, admin, coaching session, emails header)
 * - LogoLight: for white/cream backgrounds (blog, public assessment, journey, emails body)
 * - LogoIcon: 40x40 mark for favicon, small spaces, coaching session top bar
 *
 * Pass `width`/`height` (numbers or strings) to override defaults.
 * The viewBox stays at 0 0 180 52 (or 0 0 40 40 for icon) so it scales cleanly.
 */
import { useId } from "react";

export function LogoDark({ width = 220, height = 52, className = "", ...rest }) {
  const uid = useId().replace(/:/g, "");
  const gd = `riq-gd-a-${uid}`;
  const bg = `riq-bg-a-${uid}`;
  const glow = `riq-glow-a-${uid}`;
  return (
    <svg width={width} height={height} viewBox="0 0 220 52" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", overflow: "visible" }} className={className} aria-label="RoleIQ — AI-Powered Intelligence" {...rest}>
      <defs>
        <linearGradient id={gd} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C9A84C" />
          <stop offset="100%" stopColor="#F2DC8A" />
        </linearGradient>
        <linearGradient id={bg} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.01" />
        </linearGradient>
        <filter id={glow}>
          <feGaussianBlur stdDeviation="1.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <polygon points="24,2 40,2 47,15 47,37 40,50 24,50 17,37 17,15" fill={`url(#${bg})`} stroke="rgba(201,168,76,0.18)" strokeWidth="0.8" />
      <polygon points="33,7 50,44 16,44" fill="none" stroke={`url(#${gd})`} strokeWidth="1.6" strokeLinejoin="round" />
      <polygon points="33,7 50,44 16,44" fill={`url(#${gd})`} opacity="0.05" />
      <polygon points="33,18 45,40 21,40" fill={`url(#${gd})`} opacity="0.10" />
      <polygon points="33,28 42,42 24,42" fill={`url(#${gd})`} opacity="0.18" />
      <polygon points="33,36 40,44 26,44" fill={`url(#${gd})`} opacity="0.44" />
      <line x1="16" y1="44" x2="50" y2="44" stroke={`url(#${gd})`} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="33" cy="7" r="3.5" fill={`url(#${gd})`} opacity="0.2" filter={`url(#${glow})`} />
      <circle cx="33" cy="7" r="2.2" fill={`url(#${gd})`} />
      <circle cx="33" cy="7" r="1" fill="#0A1628" />
      <text x="60" y="24" fontFamily="Georgia,'Times New Roman',serif" fontSize="21" fontWeight="400" fill="#ffffff">RoleIQ</text>
      <line x1="60" y1="31" x2="218" y2="31" stroke="rgba(201,168,76,0.2)" strokeWidth="0.6" />
      <text x="60" y="44" fontFamily="system-ui,-apple-system,Arial,sans-serif" fontSize="8" fontWeight="300" fill="#C9A84C" letterSpacing="0.4" style={{ whiteSpace: "nowrap" }}>AI-Powered Intelligence</text>
    </svg>
  );
}

export function LogoLight({ width = 220, height = 52, className = "", ...rest }) {
  const uid = useId().replace(/:/g, "");
  const gd = `riq-gd-b-${uid}`;
  return (
    <svg width={width} height={height} viewBox="0 0 220 52" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", overflow: "visible" }} className={className} aria-label="RoleIQ — AI-Powered Intelligence" {...rest}>
      <defs>
        <linearGradient id={gd} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#B8942A" />
          <stop offset="100%" stopColor="#C9A84C" />
        </linearGradient>
      </defs>
      <polygon points="24,2 40,2 47,15 47,37 40,50 24,50 17,37 17,15" fill="none" stroke="rgba(15,30,53,0.07)" strokeWidth="0.8" />
      <polygon points="33,7 50,44 16,44" fill="none" stroke="#0F1E35" strokeWidth="1.6" strokeLinejoin="round" opacity="0.78" />
      <polygon points="33,7 50,44 16,44" fill="#0F1E35" opacity="0.03" />
      <polygon points="33,28 42,42 24,42" fill="#0F1E35" opacity="0.05" />
      <polygon points="33,36 40,44 26,44" fill={`url(#${gd})`} opacity="0.62" />
      <line x1="16" y1="44" x2="50" y2="44" stroke={`url(#${gd})`} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="33" cy="7" r="2.2" fill={`url(#${gd})`} />
      <circle cx="33" cy="7" r="1" fill="#ffffff" />
      <text x="60" y="24" fontFamily="Georgia,'Times New Roman',serif" fontSize="21" fontWeight="400" fill="#0F1E35">RoleIQ</text>
      <line x1="60" y1="31" x2="218" y2="31" stroke="rgba(15,30,53,0.1)" strokeWidth="0.6" />
      <text x="60" y="44" fontFamily="system-ui,-apple-system,Arial,sans-serif" fontSize="8" fontWeight="300" fill="#C9A84C" letterSpacing="0.4" style={{ whiteSpace: "nowrap" }}>AI-Powered Intelligence</text>
    </svg>
  );
}

export function LogoIcon({ width = 40, height = 40, className = "", ...rest }) {
  const uid = useId().replace(/:/g, "");
  const gd = `riq-gd-i-${uid}`;
  return (
    <svg width={width} height={height} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }} className={className} aria-label="RoleIQ" {...rest}>
      <defs>
        <linearGradient id={gd} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C9A84C" />
          <stop offset="100%" stopColor="#F2DC8A" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="9" fill="#0F1E35" />
      <polygon points="20,4 34,36 6,36" fill="none" stroke={`url(#${gd})`} strokeWidth="1.8" strokeLinejoin="round" />
      <polygon points="20,20 30,34 10,34" fill={`url(#${gd})`} opacity="0.5" />
      <line x1="6" y1="36" x2="34" y2="36" stroke={`url(#${gd})`} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="20" cy="4" r="3.5" fill={`url(#${gd})`} />
      <circle cx="20" cy="4" r="1.5" fill="#0F1E35" />
    </svg>
  );
}

export default LogoDark;
